// An organisation's own login link (`/login/<slug>`).
//
// Same email + password sign-in as the generic page, plus "Login with Google"
// when a superadmin has configured the organisation's Google client. The slug is
// what lets the button go straight to Google: each organisation brings its own
// OAuth client, so the plain /login page cannot know whose client to use.

import { useQuery } from "@tanstack/react-query";
import { useParams } from "react-router-dom";

import { Brand } from "@/components/brand";
import {
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  Skeleton,
} from "@/components/ui/primitives";
import { getSsoPublicConfig, googleSsoStartUrl } from "@/lib/api/endpoints";

import { LoginNotices, TenantForm, useAfterLogin } from "../page";

export default function OrganizationLoginPage() {
  const slug = String(useParams().slug);
  const afterLogin = useAfterLogin();
  const sso = useQuery({
    queryKey: ["sso-config", slug],
    queryFn: () => getSsoPublicConfig(slug),
    retry: false,
  });

  return (
    <main className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <div className="w-full max-w-md">
        <div className="mb-6 flex justify-center">
          <Brand />
        </div>
        <Card className="shadow-md">
          <CardHeader>
            <CardTitle>Sign in</CardTitle>
            <CardDescription>
              Organisation <code className="font-mono text-xs">{slug}</code>
            </CardDescription>
          </CardHeader>
          <CardContent>
            <LoginNotices />
            {sso.isLoading ? (
              <Skeleton className="h-10 w-full" />
            ) : sso.data?.google_enabled ? (
              <>
                <Button
                  type="button"
                  variant="outline"
                  className="w-full gap-2"
                  onClick={() => {
                    // Full-page navigation: the API redirects to Google.
                    window.location.href = googleSsoStartUrl(slug);
                  }}
                >
                  <GoogleMark />
                  Login with Google
                </Button>
                <div className="my-4 flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="h-px flex-1 bg-border" />
                  or
                  <span className="h-px flex-1 bg-border" />
                </div>
              </>
            ) : null}
            <TenantForm onDone={afterLogin} />
          </CardContent>
        </Card>
      </div>
    </main>
  );
}

function GoogleMark() {
  return (
    <svg aria-hidden="true" viewBox="0 0 48 48" className="h-4 w-4">
      <path
        fill="#FFC107"
        d="M43.6 20.5H42V20H24v8h11.3C33.7 32.7 29.2 36 24 36c-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 12.9 4 4 12.9 4 24s8.9 20 20 20 20-8.9 20-20c0-1.3-.1-2.4-.4-3.5z"
      />
      <path
        fill="#FF3D00"
        d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.8 1.2 7.9 3.1l5.7-5.7C34 6.1 29.3 4 24 4 16.3 4 9.7 8.3 6.3 14.7z"
      />
      <path
        fill="#4CAF50"
        d="M24 44c5.2 0 9.9-2 13.4-5.2l-6.2-5.2C29.2 35.1 26.7 36 24 36c-5.2 0-9.6-3.3-11.3-8l-6.5 5C9.5 39.6 16.2 44 24 44z"
      />
      <path
        fill="#1976D2"
        d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.2-4.1 5.6l6.2 5.2C37 39.2 44 34 44 24c0-1.3-.1-2.4-.4-3.5z"
      />
    </svg>
  );
}
